import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import {
  AccountTree as SitesIcon,
  MedicalServices as ProviderIcon,
  Badge as StaffIcon,
  People as PatientIcon,
  Mic as SessionIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as ClinicIcon,
} from '@mui/icons-material';
import * as orgApi from '../api/organization';
import StructureTree from '../components/StructureTree';

function Stat({ icon, label, value, color = 'primary.main' }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: color }}>{icon}</Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * The org admin's home screen: totals across the organisation, then the same numbers
 * broken out per site so an outlier is visible without opening each one.
 */
export default function OrgDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    orgApi.getDashboard()
      .then((d) => { if (alive) setData(d); })
      .catch((err) => {
        if (alive) toast.error(err.response?.data?.detail || 'Could not load your organisation');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!data) return null;

  const { organization: org, totals, sites } = data;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><SitesIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>{org.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {totals.active_sites} active site{totals.active_sites === 1 ? '' : 's'}
            {totals.sites !== totals.active_sites && ` · ${totals.sites - totals.active_sites} inactive`}
          </Typography>
        </Box>
        <Button variant="contained" sx={{ ml: 'auto' }} onClick={() => navigate('/org/sites')}>
          Manage sites
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Stat icon={<SitesIcon />} label="Sites" value={totals.sites} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Stat icon={<ProviderIcon />} label="Providers" value={totals.providers} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Stat icon={<StaffIcon />} label="Staff" value={totals.staff} color="secondary.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Stat icon={<PatientIcon />} label="Patients" value={totals.patients} color="info.main" />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Stat icon={<SessionIcon />} label="Sessions" value={totals.sessions} color="success.main" />
        </Grid>
      </Grid>

      {/* The organisation as a map — its regions and the offices in each — the same
          view an enterprise has on its Overview. */}
      <StructureTree />

      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>By site</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Site</TableCell>
              <TableCell align="right">Providers</TableCell>
              <TableCell align="right">Staff</TableCell>
              <TableCell align="right">Admins</TableCell>
              <TableCell align="right">Patients</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.map((s) => (
              <TableRow key={s.id} hover sx={{ opacity: s.is_active ? 1 : 0.55 }}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {s.type === 'hospital'
                      ? <HospitalIcon fontSize="small" color="secondary" />
                      : <ClinicIcon fontSize="small" color="primary" />}
                    <Typography fontWeight={600}>{s.name}</Typography>
                    {!s.is_active && <Chip label="Inactive" size="small" />}
                  </Stack>
                </TableCell>
                <TableCell align="right">{s.counts.providers}</TableCell>
                <TableCell align="right">{s.counts.staff}</TableCell>
                <TableCell align="right">{s.counts.admins}</TableCell>
                <TableCell align="right">{s.counts.patients}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
